/**
 * Bytescale video processing utilities
 * Handles async video processing and artifact URL resolution
 */

/**
 * Fetches and resolves the processed video URL from Bytescale
 * The /video endpoint returns a job response that we parse to get the final artifact URL
 */
async function resolveBytescaleVideoUrl(path) {
  const accountId = process.env.REACT_APP_BYTESCALE_ACCOUNT_ID;

  // Request the video processing job (without &a= parameter)
  // This returns the job JSON with the artifact URL
  // Don't include cache parameters when checking job status
  const jobUrl = `https://upcdn.io/${accountId}/video${path}?h=1080&f=mp4-h264`;

  try {
    // Make the request with explicit headers to ensure proper handling
    const response = await fetch(jobUrl, {
      method: 'GET',
      headers: {
        // Accept JSON to get job status instead of video binary
        Accept: 'application/json',
      },
      // Prevent caching this request
      // 'no-store' ensures we always get fresh job status
      cache: 'no-store',
      // Ensure cookies aren't sent which might cause issues
      credentials: 'omit',
    });

    if (!response.ok) {
      // Fallback to raw URL if job fetch fails
      return `https://upcdn.io/${accountId}/raw${path}`;
    }

    const contentType = response.headers.get('content-type');

    // If the response is video content, it means processing is complete
    if (
      contentType &&
      (contentType.includes('video/') || contentType.includes('application/octet-stream'))
    ) {
      // Return the URL with cache parameters for the processed video
      return `${jobUrl}&cache-perm=true&cache-ttl=31536000`;
    }

    const responseText = await response.text();

    let jobData;
    try {
      jobData = JSON.parse(responseText);
    } catch (parseError) {
      // If we can't parse the response, fall back to raw
      return `https://upcdn.io/${accountId}/raw${path}`;
    }

    // Check if processing succeeded and artifact is available
    if (jobData.status === 'Succeeded' && jobData.summary?.result?.artifactUrl) {
      // Return the artifact URL from the job response
      // This URL has cache-perm (with hyphen) and includes &a=/video.mp4
      return jobData.summary.result.artifactUrl;
    }

    // If still processing or pending, return raw URL as fallback
    // Note: Bytescale can return "Pending", "Processing", or "Running" for in-progress videos
    if (
      jobData.status === 'Pending' ||
      jobData.status === 'Processing' ||
      jobData.status === 'Running'
    ) {
      return `https://upcdn.io/${accountId}/raw${path}`;
    }

    // If failed, use raw URL
    return `https://upcdn.io/${accountId}/raw${path}`;
  } catch (error) {
    // Fallback to raw URL on any error
    return `https://upcdn.io/${accountId}/raw${path}`;
  }
}

export default resolveBytescaleVideoUrl;
